import { Box, Center, Flex, Heading, Link, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import React, { useContext, useEffect, useState } from 'react';
import { FaBookReader } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import ProfileContext, {
    getProfileErrorHeader,
    getProfileErrorText,
} from '../context/ProfileContext';
import SelectedGameContext from '../context/SelectedGameContext';
import AddItemForm from '../forms/AddItem';
import { ItemDetail } from '../interfaces/IInventory';
import InventoryDataCaller from '../util/inventoryDataCaller';

const initialValues: ItemDetail = {
    item_id: 'curr_coin',
    item_name: 'Bingle',
    item_desc: '',
    item_type: 'item',
    item_prop: null,
    item_owner: '6196834d-3c86-4bfb-b536-bf1ee821194b',
    creator_license: ['d2eea033-f28d-41c6-a992-f85a55c70ab2'],
    creator_user: null,
    ttl: 0,
};

type AddItemProps = {
    isSaved?: boolean;
};

export const AddItem: React.FC<AddItemProps> = ({ isSaved = false }) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const profile = useContext(ProfileContext);
    const [isEditDataLoaded, setIsEditDataLoaded] = useState(!isSaved);
    const [editData, setEditData] = useState<ItemDetail>(initialValues);
    const toast = useToast();
    const { keycloak } = useKeycloak();
    const url = `${config.inventoryServiceURL}`;
    const { id } = useParams();
    const navigate = useNavigate();

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    const saveItem = async (values: ItemDetail) => {
        setIsEditDataLoaded(false);
        const dataCaller = new InventoryDataCaller(url, keycloak.token!);
        const res = await dataCaller.updateItem(values);
        if (res.status !== 200) {
            setIsEditDataLoaded(true);
            setEditData(values);
            return toast({
                title: 'There was an error',
                description: 'Please try creating/updating the item again.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
        setIsEditDataLoaded(true);
        toast({
            title: 'Success!',
            description: 'Item has been created. Cheers!',
            status: 'success',
            duration: 9000,
            isClosable: true,
        });
        navigate(`/game/${currLicense?.key}/inventory`);
    };

    useEffect(() => {
        setEditData((item) => {
            return {
                ...item,
                item_owner: keycloak.subject || '',
            };
        });
    }, [keycloak.subject]);

    useEffect(() => {
        if (!currLicense) {
            return;
        }
        if (!id) {
            return;
        }
        const loadItem = async (itemID: string) => {
            const dataCaller = new InventoryDataCaller(url, keycloak.token!);
            const res = await dataCaller.getItem(itemID);

            if (res.status !== 200) {
                setIsEditDataLoaded(true);
                if (res.status === 404) {
                    return;
                }
                toast({
                    title: 'Error',
                    description: (await res.json()).message,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
                return;
            }

            const { data: items } = await res.json();
            setEditData(items[0]);
            setIsEditDataLoaded(true);
        };
        loadItem(id!);
    }, [currLicense, id]);

    const handleSubmit = (values: ItemDetail) => {
        saveItem(values);
    };

    const displaySection = () => {
        if (!isEditDataLoaded) {
            return (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            );
        }
        return (
            <Box>
                <Flex justifyContent={'space-between'}>
                    <Heading>Add Item</Heading>
                    <Link
                        href={`${config.docsURL}/tutorials/leaderboard-configuration-manager`}
                        isExternal
                    >
                        <Flex alignItems={'center'}>
                            <Text mr={2}>Go to Documentation</Text>
                            <FaBookReader max={'2px'}></FaBookReader>
                        </Flex>
                    </Link>
                </Flex>
                <Stack spacing={10} direction="row">
                    {editData && (
                        <AddItemForm
                            initialData={editData}
                            owner={keycloak.subject!}
                            onSubmit={handleSubmit}
                            isSaved={isSaved}
                        />
                    )}
                </Stack>
            </Box>
        );
    };
    return (
        <Box>
            {profile.errors.length > 0 ? (
                <>
                    <Heading textAlign="center" marginBottom="1em">
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : isEditDataLoaded ? (
                displaySection()
            ) : (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
            {!currLicense && profile.errors.length === 0 && (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
        </Box>
    );
};
